import { AnimatePresence, motion } from 'motion/react';
import { Menu, UserRound, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { brand } from '../../config/brand';
import { useAuth } from '../../features/auth/AuthContext';
import { ArchiveSeal } from '../ui/ArchiveSeal';
import { AudioToggle } from '../audio/AudioToggle';

const links = [
  { to: '/archivo', label: 'Archivo' },
  { to: '/sagas/yuna', label: 'Yuna' },
  { to: '/campana', label: 'El Primer Eco' },
  { to: '/personajes', label: 'Expedientes' },
];

export function SiteHeader() {
  const [open, setOpen] = useState(false);
  const closeRef = useRef<HTMLButtonElement>(null);
  const { user } = useAuth();

  useEffect(() => {
    if (!open) return;
    closeRef.current?.focus();
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
      if (event.key !== 'Tab') return;
      const menu = document.getElementById('mobile-menu');
      const focusable = menu?.querySelectorAll<HTMLElement>('a[href], button:not([disabled])');
      if (!focusable?.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last?.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first?.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  return (
    <header className="site-header">
      <Link to="/" className="site-brand" aria-label={`${brand.archiveName}, inicio`}>
        <ArchiveSeal size="small" />
        <span>
          <strong>Cárdinal</strong>
          <small>Biblioteca Terminus</small>
        </span>
      </Link>
      <nav className="desktop-nav" aria-label="Navegación principal">
        {links.map((link) => (
          <NavLink key={link.to} to={link.to}>
            {link.label}
          </NavLink>
        ))}
      </nav>
      <div className="header-actions">
        <AudioToggle compact />
        <Link className="profile-link" to={user ? '/perfil' : '/login'} aria-label={user ? 'Perfil' : 'Iniciar sesión'}>
          <UserRound aria-hidden="true" />
        </Link>
        <button
          className="menu-toggle"
          type="button"
          onClick={() => setOpen(true)}
          aria-expanded={open}
          aria-controls="mobile-menu"
          aria-label="Abrir menú"
        >
          <Menu aria-hidden="true" />
        </button>
      </div>
      <AnimatePresence>
        {open && (
          <motion.div
            id="mobile-menu"
            className="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            role="dialog"
            aria-modal="true"
            aria-label="Navegación"
          >
            <button
              ref={closeRef}
              className="mobile-menu__close"
              type="button"
              onClick={() => setOpen(false)}
              aria-label="Cerrar menú"
            >
              <X aria-hidden="true" />
            </button>
            <p className="eyebrow">Índice de registros</p>
            <nav aria-label="Navegación móvil">
              {[{ to: '/', label: 'Umbral' }, ...links, { to: user ? '/perfil' : '/login', label: user ? 'Perfil' : 'Acceso' }].map(
                (link, index) => (
                  <NavLink key={link.to} to={link.to} onClick={() => setOpen(false)}>
                    <span>0{index + 1}</span>
                    {link.label}
                  </NavLink>
                ),
              )}
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
